import { USER_ROLES } from './index';

/**
 * Named things a user can do, checked through `can()` instead of comparing roles.
 *
 * Roles are on their way out in favour of per-user permissions. Until then each capability
 * maps onto today's roles here, in one place, so the switch only changes this file and
 * nothing that asks the question.
 */
export const CAPABILITIES = [
  'clients:manage',
  'experiments:create',
  'experiments:start',
  'experiments:deploy',
  'experiments:implement',
  'pipelines:manage',
  'surveys:manage',
  'websites:manage',
  'integrations:manage',
  'team:manage',
  // Create and revoke a website's server SDK keys (PER-134).
  'server-keys:manage',
  /** The editor AI assistant: chatting, the element prompt, and applying what it proposes. */
  'editor-ai:use',
  /**
   * Reading the variant's editor AI conversation without sending: the AI panel, read-only. The one
   * capability here that is not a write, so it is the one a Member has (the server lets a Member
   * GET /editor-ai/thread and refuses their POSTs).
   */
  'editor-ai:view',
  /** Reading a website's learning cards, patterns and the writing-learnings status. A read. */
  'learnings:view',
  /** Switching a website's learnings on or off, and the account and website contribution opt-outs. */
  'learnings:manage',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

type UserRole = (typeof USER_ROLES)[number];

// Members are read-only on the server (userMiddleware allows them GET only), so every
// capability here but the reads, editor-ai:view and learnings:view, is a write, and only those
// reads reach a Member.
const ROLE_CAPABILITIES: Record<UserRole, readonly Capability[]> = {
  Member: ['editor-ai:view', 'learnings:view'],
  Admin: CAPABILITIES,
  Owner: CAPABILITIES,
  'Super Admin': CAPABILITIES,
};

type CapabilityHolder = { role?: string | null } | null | undefined;

export const can = (user: CapabilityHolder, capability: Capability): boolean => {
  const role = user?.role;
  if (!role || !Object.hasOwn(ROLE_CAPABILITIES, role)) return false;
  return ROLE_CAPABILITIES[role as UserRole].includes(capability);
};
